import { useState, useCallback, useMemo } from "react";
import type {
  StepId,
  StepDefinition,
} from "@/features/multistep/registry/types";

export interface UseFormStepper {
  currentStepId: StepId;
  goNext: () => void;
  goPrev: () => void;
  reset: () => void;
  currentStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  applicableSteps: StepDefinition[];
}

/**
 * Hook para manejar el estado de navegación entre steps
 * basado en `stepsRegistry`.
 */
export const useFormStepper = (
  steps: StepDefinition[],
  tipoDocumento: string
): UseFormStepper => {
  // 🔹 Filtramos solo los steps aplicables según allData
  const applicableSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.isApplicable) return step.isApplicable(tipoDocumento);
    });
  }, [tipoDocumento, steps]);

  const stepIds: StepId[] = applicableSteps.map((s) => s.id);

  // fallback: si no hay steps, usar "generalidades" como placeholder
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < stepIds.length - 1 ? prev + 1 : prev));
  }, [stepIds.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const reset = useCallback(() => setCurrentIndex(0), []);

  return {
    currentStepId: stepIds[currentIndex] ?? ("generalidades" as StepId),
    applicableSteps,
    currentStepIndex: currentIndex,
    goNext,
    goPrev,
    reset,
    isFirstStep: currentIndex === 0,
    isLastStep: currentIndex === stepIds.length - 1,
  };
};
